// @flow

import React, { type Element } from "react";
import classNames from "classnames";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { connect } from "react-redux";
import moment from "moment";
import config from "@config";
import type { State } from "@store/reducers";
import { removeNullAndUndefinedProps } from "@transformers/object";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  LinearWizard,
  Step,
  View,
  SchemaForm,
} from "@components";
import sharedActionCreators from "@modules/shared/actionCreator";
import actionCreators from "@modules/reporting/actionCreator";
import * as sharedSelectors from "@modules/shared/selectors";
import type {
  AddEditModalConfig,
  AddEditModalStepConfig,
  AddEditReportModalType,
  DefaultReport,
  MySavedReport,
} from "@modules/reporting/reducer/modals/types";
import { ReportModalStepTypes } from "@modules/reporting/constants/reports";
import { getAddEditReportModal } from "@modules/reporting/selectors/modals";
import StepAreas from "./step-areas";
import StepFilters from "./step-filters";
import StepTable from "./step-table";
import Ajv from "ajv";
import { groupHasDuplicates } from "@transformers/array";
import { removeEmptyGroupsFromObjectProperties } from "@transformers/object";
import ReportAreas from "../report-areas";
import type { ReportAreasField } from "@modules/reporting/reducer/types";
type OwnProps = $Exact<{
  clientId: number,
  modalConfig: AddEditModalConfig,
  rightPanel?: boolean,
  initialStep?: number,
  getReport: (data: DefaultReport) => void,
  updateReport: (data: MySavedReport) => void,
  onChange?: (data: Object) => void,
  onClose: () => void,
  onSubmitWithoutRedirect: (data: Object) => void,
  modalTitle?: string,
  submitButtonTitle?: string,
  className?: string,
  withoutRedirect?: boolean,
}>;

type PropsFromState = $Exact<{
  hierarchyTree: ?Object,
  modal: AddEditReportModalType,
}>;

type PropsFromDispatch = $Exact<{
  getActiveHierarchyTree: typeof sharedActionCreators.GetMyHierarchyTree,
  getActiveHierarchyTreeNew: typeof sharedActionCreators.GetMyHierarchyTreeNew,
  toggleAddEditReportModal: typeof actionCreators.ToggleAddEditReportModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

type ComponentState = $Exact<{
  wizardData: Object,
  filters: Object,
  showAreasWarning: boolean,
  isWizardValid: boolean,
}>;

export class AddEditReportModal extends React.Component<Props, ComponentState> {
  constructor(props: Props) {
    super(props);
    this.formRef = React.createRef();
    this.stepFormRef = React.createRef();
  }

  static defaultProps = {
    onClose: () => {},
  };
  state = {
    isWizardValid: false,
    wizardData: {},
    filters: this.props.modal?.data?.filters,
    selectedOrganisationalUnitIds:
      this.props.modal?.data?.filters.selectedOrganisationalUnitIds || [],
    excludedOrganisationalUnitIds:
      this.props.modal?.data?.filters.excludedOrganisationalUnitIds || [],
    showAreasWarning: false,
    filtersErrors: [],
  };

  componentDidMount() {
    if (this.props.modal.getNewData === true) {
      // Currently getNewData is used ONLY in focus lists managers and getActiveHierarchyTreeNew() must be used ONLY there
      // change this accordingly
      this.props.getActiveHierarchyTreeNew({
        clientId: this.props.clientId,
        showEmployees: false,
      });
    } else {
      this.props.getActiveHierarchyTree({
        clientId: this.props.clientId,
        showEmployees: false,
      });
    }
  }

  onWizardChange = (wizardData: Array<Object> = [], isValid?: boolean) => {
    if (
      !groupHasDuplicates(((wizardData[1] || {}).filters || {}).staffGroups) &&
      !groupHasDuplicates(((wizardData[1] || {}).filters || {}).alertTypes) &&
      !groupHasDuplicates(((wizardData[1] || {}).filters || {}).ageGroups)
    ) {
      let wData;
      wizardData.forEach(obj => {
        wData = { ...wData, ...obj };
      });
      const data = { ...this.state.wizardData, ...wData };
      this.props.onChange && this.props.onChange(data);

      this.setState({
        ...this.state,
        ...wizardData[0],
        wizardData: data,
        filters: wizardData[1].filters,
        isWizardValid: isValid,
        showAreasWarning: false,
      });
    }
  };

  onSubmitForm = () => {
    if (!this.props.modal.data) {
      this.handleGetReport();
    } else {
      this.handleUpdateReport();
    }
  };

  handleGetReport = () => {
    if (this.props.getReport) {
      this.props.getReport({
        name: this.props.t("NEW_REPORT"),
        filters: removeEmptyGroupsFromObjectProperties(this.filtersData),
      });
    }
  };
  handleFiltersErrors = (errors: Object[]) => {
    this.setState(() => {
      return { ...this.state, filtersErrors: errors };
    });
  };

  handleUpdateReport = () => {
    this.props.updateReport({
      ...this.props.modal.data,
      filters: {
        ...removeEmptyGroupsFromObjectProperties({
          ...(this.props.modal.data || {}).filters,
        }),
        ...this.filtersData,
      },
    });
  };

  handleCloseModal = () => {
    this.props.toggleAddEditReportModal({ visible: false });
    this.props.onClose();
  };

  handleShowWarning = () => this.setState({ showAreasWarning: true });

  get filtersData() {
    return this.state.wizardData.filters
      ? {
          ...this.state.wizardData.filters,
          excludedOrganisationalUnitIds: this.state.excludedOrganisationalUnitIds,
          selectedOrganisationalUnitIds: this.state.selectedOrganisationalUnitIds,
        }
      : this.props.modal.data?.filters;
  }

  get submitButtonTitle() {
    if (this.props.submitButtonTitle) {
      return this.props.submitButtonTitle;
    }
    return this.props.modal.data
      ? this.props.t("RUN_REPORT_DEFAULT")
      : this.props.t("GENERATE_REPORT");
  }

  get validateAreas() {
    if (((this.state.wizardData || {}).selectedOrganisationalUnitIds || []).length < 1) {
      return false;
    }
    return true;
  }
  get validateFilters() {
    return !!this.filtersData && this.state.filtersErrors.length === 0;

    // Leave this commented code untile refactor this compoenent
    // if (this.formRef.current) {
    //   return this.formRef.current.state.errors.length === 0;
    // } else {
    //   const formData = this?.state?.wizardData?.filters || this.props?.modal?.data?.filters;
    //   if (this.schema) {
    //     const ajv = new Ajv(this.schema);
    //     const isValid =
    //       ajv.validate && ajv.validate(this.schema, removeNullAndUndefinedProps(formData));
    //     const errors = ajv.errors;
    //     if (errors) console.warn(errors);

    //     return !!!errors;
    //   }
    // }
  }

  get isValid() {
    return this.validateAreas && this.validateFilters;
  }

  handleTreeUpdate = (data: Object) => {
    this.setState({
      ...this.state,
      excludedOrganisationalUnitIds: data.excluded,
      selectedOrganisationalUnitIds: data.selected,
      wizardData: {
        ...this.state.wizardData,
        excludedOrganisationalUnitIds: data.excluded,
        selectedOrganisationalUnitIds: data.selected,
        filters: {
          ...this.state.wizardData.filters,
          excludedOrganisationalUnitIds: data.excluded,
          selectedOrganisationalUnitIds: data.selected,
        },
      },
    });
  };

  resetAreas = () => {
    this.setState({
      ...this.state,
      excludedOrganisationalUnitIds: [],
      selectedOrganisationalUnitIds: [],
    });
    this.handleShowWarning();
  };

  handleFiltersChange = (formData: Object) => {
    if (moment(formData.toDate).isBefore(moment(formData.fromDate))) {
      formData.toDate = formData.fromDate;
    }

    this.onWizardChange([{}, { filters: formData }], this.validateFilters);
  };
  handleFiltersSubmit = () => {
    this.props.updateReport({
      ...this.props.modal.data,
      filters: this.state.wizardData.filters,
    });
  };
  updateUiSchemaBasedOnFormData = (uiSchema: Object) => {
    if (!this.formData) return uiSchema;
    const disabled = this.formData.dateRange !== "CUSTOM" && this.formData.dateRange !== null;
    return {
      ...uiSchema,
      fromDate: {
        ...uiSchema.fromDate,
        "ui:disabled": disabled,
      },
      toDate: {
        ...uiSchema.toDate,
        "ui:disabled": disabled,
      },
    };
  };
  get formData() {
    const formData = this.state?.wizardData?.filters || this.props.modal.data?.filters;
    const erase = formData && formData.dateRange !== "CUSTOM" && formData.dateRange !== null;
    if (erase) {
      formData.fromDate = null;
      formData.toDate = null;
    }
    return formData;

    // return this.props.modal.data?.filters || this.state?.wizardData?.filters;
  }
  get schema() {
    return this.props.modalConfig[0].schema;
  }
  get uiSchema() {
    const scm = this.props.modalConfig[0].uiSchema;
    const uischema = {
      ...scm,
      toDate: {
        ...scm.toDate,
        "ui:options": {
          ...scm.toDate["ui:options"],
          minDate: this.formData?.fromDate && new Date(this.formData.fromDate).toISOString(),
        },
      },
    };
    return this.updateUiSchemaBasedOnFormData(uischema);
  }
  getStepTitle = (titleString: $Exact<{ new: string, edit: string }>) => {
    return this.props.modal.data ? titleString.edit : titleString.new;
  };
  getModalStep = (stepConfig: AddEditModalStepConfig, index: number): Element<typeof Step> => {
    const withTableStep = this.props.modalConfig.find(config => config.type === "table");
    switch (stepConfig.type) {
      case ReportModalStepTypes.FILTERS:
        return (
          <Step
            key={`step-${index}`}
            component={StepFilters}
            title={this.props.t("FILTERS")}
            schema={this.schema}
            uiSchema={this.uiSchema}
            prevStepDone={this.state.wizardData.selectedOrganisationalUnitIds}
            filters={this.filtersData}
            onChange={this.onChange}
            onError={this.handleFiltersErrors}
            submitForm={this.onSubmitForm}
            enableNext={this.props.modalConfig[1]?.type === "table"}
          />
        );
      case ReportModalStepTypes.TABLE:
      default:
        return (
          <Step
            key={`step-${index}`}
            component={StepTable}
            title={this.getStepTitle(stepConfig.title)}
            rawData={stepConfig.rawData}
            columnsData={stepConfig.columnsData}
            selectedItems={this.state.wizardData.selectedItems}
            filtersData={this.filtersData}
          />
        );
    }
  };
  get steps() {
    return [
      <Step
        key="areas"
        component={StepAreas}
        title={this.props.t("HIERARCHY")}
        hierarchyTree={this.props.hierarchyTree}
        reportData={this.props.modal.data}
        handleShowWarning={this.handleShowWarning}
      />,
      ...this.props.modalConfig.map((stepConfig: Object, index: number) =>
        this.getModalStep(stepConfig, index),
      ),
    ];
  }
  handleSubmitForm = () => {
    this.onSubmitForm();
  };
  onError = (errors: Array<Object>) => {
    console.log("err");
  };
  render() {
    if (!this.props.modal.visible) return;
    if (this.props.modal.data && this.props.rightPanel !== false) {
      // Edit modal
      return (
        <Modal
          onToggle={this.handleCloseModal}
          rightPanel
          width={this.props.modal.initialStep == 2 ? "30vw" : "50vw"}
        >
          <View flex={1} padding={[0, 6, 6, 6]} maxWidth="100%" gap={3}>
            {/* Modal header */}
            <View
              direction="row"
              separator="bottom"
              padding={[3, 0, 3, 0]}
              backgroundColor={"white"}
              position="sticky"
              zIndex={5}
              top={0}
              justifyContent="space-between"
            >
              <View flex={1}>
                <h4>{this.props.t(this.props.modal.initialStep == 2 ? "FILTERS" : "SCOPE")}</h4>
              </View>
              <View gap={3} direction="row">
                <Button
                  onClick={this.handleCloseModal}
                  color="secondary"
                  label={this.props.t("CANCEL")}
                />

                <Button
                  onClick={this.handleUpdateReport}
                  disabled={this.props.modal.initialStep == 2 && !this.validateFilters}
                  color="primary"
                  materialIcon="replay_circle_filled"
                  label={this.props.t("SUBMIT")}
                />
              </View>
            </View>

            <View>
              {/* Modal content */}
              {this.props.modal.initialStep == 1 && this.props.hierarchyTree && (
                <ReportAreas
                  organizationStructure={this.props.hierarchyTree}
                  editable={true}
                  excluded={this.state.excludedOrganisationalUnitIds || []}
                  selected={this.state.selectedOrganisationalUnitIds || []}
                  onTreeUpdate={this.handleTreeUpdate}
                  resetAreas={this.resetAreas}
                />
              )}
              {this.props.modal.initialStep == 2 && (
                <SchemaForm
                  ref={this.formRef}
                  onChange={this.handleFiltersChange}
                  onError={this.handleFiltersErrors}
                  formData={this.formData}
                  schema={this.schema}
                  liveValidate={true}
                  hideLiveErrors={true}
                  uiSchema={this.uiSchema}
                  externalSubmit
                />
              )}
            </View>
          </View>
        </Modal>
      );
    } else if (this.props.modal.data && this.props?.modalConfig[1]?.type === "table") {
      // Mi Presentations
      return (
        <Modal className={classNames("modal-large", this.props.className)}>
          <ModalHeader toggle={this.handleCloseModal}>
            {this.props.t("MI_PRESENTATION")}
          </ModalHeader>
          <ModalBody className="p-0 pb-3">
            {this.state.showAreasWarning && (
              <div className="error-warning w-80 mt-2">
                {this.props.t("MISSING_DEPARTMENT_WARNING")}
              </div>
            )}
            <LinearWizard
              data={[...new Array(this.props.initialStep || 1)].map(() => ({
                data: {},
                valid: true,
              }))}
              defaultStep={this.props.initialStep}
              onChange={this.onWizardChange}
            >
              {this.steps}
            </LinearWizard>
          </ModalBody>
          <ModalFooter className="d-flex justify-content-end">
            <Button color="secondary" onClick={this.handleCloseModal}>
              {this.props.t("CANCEL")}
            </Button>
            <Button
              materialIcon="replay_circle_filled"
              disabled={!this.isValid}
              onClick={this.handleSubmitForm}
            >
              {this.props.t("SUBMIT")}
            </Button>
          </ModalFooter>
        </Modal>
      );
    } else {
      // Create modal
      return (
        <Modal className={classNames("modal-large", this.props.className)}>
          <ModalHeader toggle={this.handleCloseModal}>{this.props.t("NEW_REPORT")}</ModalHeader>
          <ModalBody className="p-0 pb-3">
            {this.state.showAreasWarning && (
              <div className="error-warning w-80 mt-2">
                {this.props.t("MISSING_DEPARTMENT_WARNING")}
              </div>
            )}

            <LinearWizard
              data={[...new Array(this.props.initialStep || 1)].map(() => ({
                data: {},
                valid: true,
              }))}
              defaultStep={this.props.initialStep}
              onChange={this.onWizardChange}
            >
              <Step
                key="areas"
                component={StepAreas}
                title={this.props.t("HIERARCHY")}
                hierarchyTree={this.props.hierarchyTree}
                reportData={this.props.modal.data}
                handleShowWarning={this.handleShowWarning}
              />
              <Step
                component={StepFilters}
                title={this.props.t("SET_FILTERS")}
                schema={this.schema}
                uiSchema={this.uiSchema}
                prevStepDone={this.state.wizardData.selectedOrganisationalUnitIds}
                filters={this.filtersData}
                onChange={this.onChange}
                onError={this.handleFiltersErrors}
                submitForm={this.onSubmitForm}
                enableNext={this.props.modalConfig[1]?.type == "table"}
              />
              {this.props.modalConfig[1]?.type == "table" && (
                <Step
                  key={`step-table`}
                  component={StepTable}
                  title={this.props.modalConfig[1].title.new}
                  rawData={this.props.modalConfig[1].rawData}
                  columnsData={this.props.modalConfig[1].columnsData}
                  selectedItems={this.state.wizardData.selectedItems}
                  filtersData={this.filtersData}
                />
              )}
            </LinearWizard>
          </ModalBody>
          <ModalFooter className="d-flex justify-content-end">
            <Button color="secondary" onClick={this.handleCloseModal}>
              {this.props.t("CANCEL")}
            </Button>
            <Button
              materialIcon="replay_circle_filled"
              disabled={!this.isValid}
              onClick={this.handleSubmitForm}
            >
              {this.props.t("SUBMIT")}
            </Button>
          </ModalFooter>
        </Modal>
      );
    }
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  hierarchyTree: sharedSelectors.getMyHierarchyTree(state),
  modal: getAddEditReportModal(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getActiveHierarchyTree: sharedActionCreators.GetMyHierarchyTree,
  getActiveHierarchyTreeNew: sharedActionCreators.GetMyHierarchyTreeNew,
  toggleAddEditReportModal: actionCreators.ToggleAddEditReportModal,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  localize(),
)(AddEditReportModal);
